import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, GitCompareArrows, Sun, Cloud, CloudRain, Snowflake, Music, Clapperboard, Tv, BookOpen, Newspaper } from "lucide-react";
import { fetchRegions, postCompare } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { displayTemp } from "@/lib/weather";

const MIN_DATE = "1900-01-01";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function weatherIcon(cond = "") {
  const c = cond.toLowerCase();
  if (c.includes("snow")) return <Snowflake size={28} />;
  if (c.includes("rain") || c.includes("storm") || c.includes("shower")) return <CloudRain size={28} />;
  if (c.includes("cloud") || c.includes("overcast") || c.includes("fog")) return <Cloud size={28} />;
  return <Sun size={28} />;
}

export default function Compare() {
  const [regions, setRegions] = useState([]);
  const [params, setParams] = useSearchParams();
  const [dateA, setDateA] = useState(params.get("date_a") || "1969-07-20");
  const [regionA, setRegionA] = useState(params.get("region_a") || "US");
  const [dateB, setDateB] = useState(params.get("date_b") || "1989-11-09");
  const [regionB, setRegionB] = useState(params.get("region_b") || "DE");
  const [loading, setLoading] = useState(false);
  const [pair, setPair] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent("page_view", { page: "compare" });
    const loadRegions = async (attempt = 1) => {
      try {
        const data = await fetchRegions();
        setRegions(data);
      } catch (err) {
        if (attempt < 2) {
          setTimeout(() => loadRegions(attempt + 1), 800);
        } else {
          toast.error("Failed to load regions");
        }
      }
    };
    loadRegions();
  }, []);

  const run = async () => {
    if (dateA < MIN_DATE || dateB < MIN_DATE) {
      toast.error("Dates must be 1900 or later");
      return;
    }
    if (dateA > todayISO() || dateB > todayISO()) {
      toast.error("No time-jumping into the future");
      return;
    }
    setLoading(true);
    setPair(null);
    // Sync URL so the comparison is shareable
    setParams({ date_a: dateA, region_a: regionA, date_b: dateB, region_b: regionB });
    try {
      trackEvent("compare_run", { date_a: dateA, region_a: regionA, date_b: dateB, region_b: regionB });
      const data = await postCompare({ date_a: dateA, region_a: regionA, date_b: dateB, region_b: regionB });
      setPair(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compare failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full" data-testid="compare-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <button
            data-testid="compare-back-btn"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink px-3 py-2 bg-paper-surface shadow-hard-sm hover:shadow-hard-hover"
            style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
          >
            <ArrowLeft size={14} /> Back
          </button>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-muted">The Split Edition · Two days, one page</span>
        </div>

        <header className="text-center border-b-4 border-double border-ink pb-4 mb-8">
          <h1 className="font-headline font-black uppercase tracking-tighter text-4xl sm:text-6xl md:text-7xl">
            Compare <span className="text-press-red">Two Days</span>
          </h1>
          <p className="mt-2 font-mono text-xs uppercase tracking-[0.3em] text-ink-muted">
            Pick any two dates — see their front pages side-by-side
          </p>
        </header>

        {/* Picker */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t-2 border-l-2 border-ink" data-testid="compare-picker">
          <div className="border-r-2 border-b-2 border-ink p-6 bg-paper-surface">
            <div className="flex items-center gap-2 mb-3">
              <span className="ink-stamp">Left Page — A</span>
            </div>
            <label className="block font-mono text-[11px] uppercase tracking-widest text-ink-muted mb-1">Date</label>
            <input
              data-testid="compare-date-a"
              type="date" value={dateA} min={MIN_DATE} max={todayISO()}
              onChange={(e) => setDateA(e.target.value)}
              className="bg-transparent border-0 border-b-2 border-ink rounded-none w-full font-mono text-xl py-2 focus:outline-none focus:border-press-red"
            />
            <label className="block font-mono text-[11px] uppercase tracking-widest text-ink-muted mt-4 mb-1">Region</label>
            <select
              data-testid="compare-region-a"
              value={regionA} onChange={(e) => setRegionA(e.target.value)}
              className="bg-transparent border-0 border-b-2 border-ink rounded-none w-full font-mono text-lg py-2 focus:outline-none focus:border-press-red appearance-none cursor-pointer"
            >
              {regions.map((r) => (<option key={r.code} value={r.code}>{r.flag} {r.name}</option>))}
            </select>
          </div>
          <div className="border-r-2 border-b-2 border-ink p-6 bg-paper-alt">
            <div className="flex items-center gap-2 mb-3">
              <span className="ink-stamp red">Right Page — B</span>
            </div>
            <label className="block font-mono text-[11px] uppercase tracking-widest text-ink-muted mb-1">Date</label>
            <input
              data-testid="compare-date-b"
              type="date" value={dateB} min={MIN_DATE} max={todayISO()}
              onChange={(e) => setDateB(e.target.value)}
              className="bg-transparent border-0 border-b-2 border-ink rounded-none w-full font-mono text-xl py-2 focus:outline-none focus:border-press-red"
            />
            <label className="block font-mono text-[11px] uppercase tracking-widest text-ink-muted mt-4 mb-1">Region</label>
            <select
              data-testid="compare-region-b"
              value={regionB} onChange={(e) => setRegionB(e.target.value)}
              className="bg-transparent border-0 border-b-2 border-ink rounded-none w-full font-mono text-lg py-2 focus:outline-none focus:border-press-red appearance-none cursor-pointer"
            >
              {regions.map((r) => (<option key={r.code} value={r.code}>{r.flag} {r.name}</option>))}
            </select>
          </div>
        </section>

        <div className="my-6 flex justify-center">
          <button
            data-testid="compare-run-btn"
            onClick={run}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-press-red text-paper font-headline font-bold uppercase tracking-widest text-lg px-8 py-4 border-2 border-ink shadow-hard hover:shadow-hard-hover disabled:opacity-70"
            style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
          >
            <GitCompareArrows size={18} /> {loading ? "Rolling both presses…" : "Compare Dates"}
          </button>
        </div>

        {/* Results */}
        {pair && (
          <>
            <div className="flex justify-center mb-4">
              <button
                data-testid="compare-copy-btn"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(window.location.href);
                    toast.success("Compare link copied");
                    trackEvent("compare_share");
                  } catch { toast.error("Could not copy — check your browser"); }
                }}
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest border-2 border-ink px-3 py-2 bg-paper-surface shadow-hard-sm hover:shadow-hard-hover"
                style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
              >
                Copy Comparison Link ↗
              </button>
            </div>
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-t-2 border-l-2 border-ink" data-testid="compare-results">
              <CompareColumn side="A" doc={pair.a} />
              <CompareColumn side="B" doc={pair.b} />
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function CompareColumn({ side, doc }) {
  const s = doc.snapshot;
  const meta = s._meta || {};
  const dt = displayTemp(s.weather, meta.region);
  return (
    <article
      className="border-r-2 border-b-2 border-ink p-6 bg-paper-surface"
      data-testid={`compare-col-${side}`}
    >
      <div className="border-b-4 border-double border-ink pb-3 mb-4 text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">
          {meta.region?.flag} {meta.region?.name} · {meta.weekday}
        </div>
        <div className="font-headline text-2xl sm:text-3xl font-black uppercase tracking-tight">{meta.long_date}</div>
      </div>

      {/* Weather + #1 song row */}
      <div className="grid grid-cols-2 gap-0 border-t-2 border-l-2 border-ink mb-4">
        <div className="border-r-2 border-b-2 border-ink p-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted flex items-center gap-1">Weather</div>
          <div className="flex items-center gap-2 mt-1">
            {weatherIcon(s.weather?.condition)}
            <div>
              <div className="font-headline text-2xl font-black leading-none">{dt.high}°/{dt.low}°{dt.unit}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{s.weather?.condition}</div>
            </div>
          </div>
        </div>
        <div className="border-r-2 border-b-2 border-ink p-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted flex items-center gap-1"><Music size={11} /> #1 Song</div>
          <div className="font-headline text-lg font-bold leading-tight mt-1">{s.music?.top_songs?.[0]?.title || "—"}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{s.music?.top_songs?.[0]?.artist}</div>
        </div>
      </div>

      {/* News */}
      <div className="mb-4">
        <div className="flex items-center gap-2 border-b-2 border-ink pb-2 mb-3">
          <Newspaper size={13} />
          <h3 className="font-mono uppercase tracking-[0.2em] text-[11px]">— Front Page —</h3>
        </div>
        <div className="space-y-3">
          {(s.news || []).slice(0, 3).map((n, i) => (
            <div key={i} className="border-b border-dotted border-ink pb-2 last:border-b-0">
              <h4 className={`font-headline font-bold uppercase leading-tight ${i === 0 ? "text-lg" : "text-base"}`}>{n.headline}</h4>
              <p className="mt-1 font-body text-sm text-ink-secondary line-clamp-3">{n.summary}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Cinema + TV + Book compact */}
      <div className="grid grid-cols-1 gap-0 border-t-2 border-l-2 border-ink">
        <MiniRow icon={<Clapperboard size={12} />} label="Top Film" value={s.cinema?.top_movies?.[0]?.title} />
        <MiniRow icon={<Tv size={12} />} label="On TV" value={s.tv?.popular_shows?.[0]?.title} />
        <MiniRow icon={<BookOpen size={12} />} label="Bestseller" value={s.books?.bestsellers?.[0]?.title} />
      </div>

      {/* Ambient */}
      <div className="mt-4 p-3 bg-ink text-paper">
        <div className="font-mono text-[10px] uppercase tracking-widest text-paper/70 mb-1">Ambient</div>
        <div className="font-mono text-[11px] uppercase tracking-widest">Gas: {s.ambient?.gas_price || "—"} · {s.ambient?.stock_index?.name}: {s.ambient?.stock_index?.value}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-paper/70 mt-1">Slang: {(s.ambient?.slang || []).slice(0, 3).join(" · ")}</div>
      </div>
    </article>
  );
}

function MiniRow({ icon, label, value }) {
  return (
    <div className="border-r-2 border-b-2 border-ink p-2 flex items-center gap-2 bg-paper-alt">
      <span className="font-mono text-[10px] uppercase tracking-widest text-ink-muted flex items-center gap-1 min-w-[80px]">{icon} {label}</span>
      <span className="font-headline font-bold leading-tight text-sm truncate">{value || "—"}</span>
    </div>
  );
}
